// Generates a standalone "financial summary" PDF from the live quote
// calculator state and downloads it as a file. This is deliberately NOT the
// web page: no toggles, no marketing copy, no interactivity — just a clean,
// paginated quote listing every service the proposal offered, what the client
// included, and what they left out (shown at qty 0 / €0), suitable to attach
// to a contract as an annex.
//
// We build a real vector PDF with pdfmake rather than printing the DOM: it
// downloads straight to a file (no print dialog / popup), looks consistent
// regardless of the user's browser print settings, and — crucially — ships
// the Roboto font, which covers Lithuanian diacritics that the standard PDF
// fonts (WinAnsi) do not.

import type {
  Content,
  TableCell,
  TDocumentDefinitions,
} from "pdfmake/interfaces";
import {
  computeProposalTotals,
  buildQuoteDocumentLines,
  type CalculatorState,
  type ProductCatalogItem,
  type QuoteDocumentLine,
} from "@/components/proposal/calculator";
import { formatEur } from "@/components/proposal/shared";
import { getProposalTexts, type ProposalLanguage } from "@/components/proposal/i18n";

export type QuoteDocumentMeta = {
  quoteNumber: string | null;
  preparedAt: string | null;
  validUntil: string | null;
  clientCompany: string | null;
  clientPerson: string | null;
  preparerName: string;
  preparerEmail: string;
};

// Derives the quote document meta (number, dates, client + preparer details)
// from a proposal's stored `variables` and creation date. Mirrors the inline
// derivation the public landing page does so the admin-side "Generate quote"
// button (opportunity Documents tab) produces an identical document. The
// validity window is the same 30 days the landing page advertises.
export function buildQuoteMetaFromVariables(
  variables: Record<string, unknown> | null | undefined,
  createdAt: string | Date | null | undefined,
  expiresAt?: string | Date | null
): QuoteDocumentMeta {
  const vars = variables ?? {};
  const client = (vars.client as Record<string, unknown> | undefined) ?? {};
  const preparer = (vars.preparer as Record<string, unknown> | undefined) ?? {};
  const quoteNumber =
    (vars.quoteNumber as string | undefined) ??
    (vars.quote_number as string | undefined) ??
    null;
  const formatYmd = (d: Date) => {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const base = createdAt ? new Date(createdAt) : null;
  const valid = base && !Number.isNaN(base.getTime()) ? base : null;
  const preparedAt = valid ? formatYmd(valid) : null;
  // An explicit expiresAt (set by an admin from the quote builder) always
  // wins. Otherwise fall back to the legacy implicit rule: 30 days from
  // creation. Mirrors the derivation in src/pages/public-proposal.tsx.
  const validUntil = (() => {
    const explicit = expiresAt ? new Date(expiresAt) : null;
    if (explicit && !Number.isNaN(explicit.getTime())) return formatYmd(explicit);
    if (!valid) return null;
    const expiry = new Date(valid);
    expiry.setUTCDate(expiry.getUTCDate() + 30);
    return formatYmd(expiry);
  })();
  const clientCompany =
    (client.company as string | undefined) ??
    (vars.clientCompany as string | undefined) ??
    null;
  const clientPerson =
    (client.name as string | undefined) ??
    (client.person as string | undefined) ??
    null;
  const preparerName =
    (preparer.name as string | undefined) ??
    (vars.preparedBy as string | undefined) ??
    "Noium team";
  const preparerEmail =
    (preparer.email as string | undefined) ??
    (vars.preparedEmail as string | undefined) ??
    "sales@noium.com";
  return {
    quoteNumber,
    preparedAt,
    validUntil,
    clientCompany,
    clientPerson,
    preparerName,
    preparerEmail,
  };
}

type DocTexts = {
  docTitle: string;
  docSubtitle: string;
  quoteNumberLabel: string;
  preparedLabel: string;
  validUntilLabel: string;
  clientLabel: string;
  preparedByLabel: string;
  recurringHeading: string;
  oneTimeHeading: string;
  colService: string;
  colUnitPrice: string;
  colQty: string;
  colAmount: string;
  colStatus: string;
  included: string;
  notIncluded: string;
  from: string;
  perMonth: string;
  totalsHeading: string;
  totalMonthly: string;
  totalMonthlyFixed: string;
  totalMonthlyUsage: string;
  totalAnnual: string;
  totalOneTime: string;
  includesNote: (amount: string, unit: string | null) => string;
  generatedNote: (date: string) => string;
  pageLabel: (current: number, total: number) => string;
};

const DOC_TEXTS: Record<ProposalLanguage, DocTexts> = {
  lt: {
    docTitle: "Finansinė pasiūlymo dalis",
    docSubtitle: "Priedas prie sutarties",
    quoteNumberLabel: "Pasiūlymo Nr.",
    preparedLabel: "Parengta",
    validUntilLabel: "Galioja iki",
    clientLabel: "Klientas",
    preparedByLabel: "Parengė",
    recurringHeading: "Mėnesinės paslaugos",
    oneTimeHeading: "Vienkartinės paslaugos",
    colService: "Paslauga",
    colUnitPrice: "Vieneto kaina",
    colQty: "Kiekis",
    colAmount: "Suma",
    colStatus: "Įtraukta",
    included: "Taip",
    notIncluded: "Ne",
    from: "Nuo",
    perMonth: "/ mėn.",
    totalsHeading: "Suvestinė",
    totalMonthly: "Mėnesinė suma",
    totalMonthlyFixed: "Pastovus mėnesinis mokestis",
    totalMonthlyUsage: "Kintantis mėnesinis mokestis (Spėjamas)",
    totalAnnual: "Metinė suma (-20%)",
    totalOneTime: "Vienkartinė suma",
    includesNote: (amount, unit) =>
      unit ? `Įskaičiuota ${amount} ${unit}` : `Įskaičiuota ${amount}`,
    generatedNote: (date) => `Dokumentas sugeneruotas ${date}`,
    pageLabel: (current, total) => `${current} iš ${total}`,
  },
  en: {
    docTitle: "Financial summary",
    docSubtitle: "Contract annex",
    quoteNumberLabel: "Quote no.",
    preparedLabel: "Prepared",
    validUntilLabel: "Valid until",
    clientLabel: "Client",
    preparedByLabel: "Prepared by",
    recurringHeading: "Recurring services",
    oneTimeHeading: "One-time services",
    colService: "Service",
    colUnitPrice: "Unit price",
    colQty: "Qty",
    colAmount: "Amount",
    colStatus: "Included",
    included: "Yes",
    notIncluded: "No",
    from: "From",
    perMonth: "/ mo",
    totalsHeading: "Summary",
    totalMonthly: "Monthly total",
    totalMonthlyFixed: "Fixed monthly fee",
    totalMonthlyUsage: "Variable monthly fee (Estimated)",
    totalAnnual: "Annual total (-20%)",
    totalOneTime: "One-time total",
    includesNote: (amount, unit) =>
      unit ? `Includes ${amount} ${unit}` : `Includes ${amount}`,
    generatedNote: (date) => `Document generated ${date}`,
    pageLabel: (current, total) => `${current} of ${total}`,
  },
};

// Palette — kept in sync with the on-screen quote styling.
const COLOR = {
  ink: "#111827",
  body: "#1f2937",
  muted: "#6b7280",
  faint: "#9ca3af",
  rule: "#e5e7eb",
  green: "#047857",
  panel: "#111827",
  panelRule: "#374151",
};

// Minimal slice of the pdfmake API we use — lets us type the dynamically
// imported module without a value import (which would pull the ~2 MB library
// into the main bundle).
type PdfDocument = {
  download: (fileName?: string) => Promise<void>;
  getBase64: () => Promise<string>;
};
type PdfMake = {
  addVirtualFileSystem: (vfs: Record<string, string>) => void;
  createPdf: (def: TDocumentDefinitions) => PdfDocument;
};

let fontsRegistered = false;
function ensureFonts(pdfMake: PdfMake, vfs: Record<string, string>) {
  if (fontsRegistered) return;
  // pdfmake 0.3 keeps the virtual file system (the bundled Roboto TTFs)
  // separate from the library; register it once before the first render.
  pdfMake.addVirtualFileSystem(vfs);
  fontsRegistered = true;
}

function unitPriceCell(line: QuoteDocumentLine, t: DocTexts, isOneTime: boolean): TableCell {
  const prefix = line.isTiered ? `${t.from} ` : "";
  const unitSuffix = line.unitName ? ` / ${line.unitName}` : "";
  const periodSuffix = isOneTime ? "" : ` ${t.perMonth}`;
  const priceText = `${prefix}${formatEur(line.unitAmountCents)}${unitSuffix}${periodSuffix}`;
  const color = line.included ? COLOR.body : COLOR.faint;
  const stack: Content[] = [{ text: priceText, color, alignment: "right" }];
  if (line.freeUpTo != null) {
    stack.push({
      text: t.includesNote(line.freeUpTo.toLocaleString("en-US"), line.unitName),
      style: "rowNote",
      alignment: "right",
    });
  }
  return { stack, margin: [0, 0, 0, 0] };
}

function serviceTable(heading: string, lines: QuoteDocumentLine[], t: DocTexts, isOneTime: boolean): Content[] {
  if (lines.length === 0) return [];
  const header: TableCell[] = [
    { text: t.colService, style: "colHead" },
    { text: t.colUnitPrice, style: "colHead", alignment: "right" },
    { text: t.colQty, style: "colHead", alignment: "right" },
    { text: t.colAmount, style: "colHead", alignment: "right" },
    { text: t.colStatus, style: "colHead", alignment: "center" },
  ];

  const body: TableCell[][] = lines.map((line) => {
    const nameColor = line.included ? COLOR.ink : COLOR.faint;
    const amountColor = line.included ? COLOR.ink : COLOR.faint;
    const statusColor = line.included ? COLOR.green : COLOR.faint;
    return [
      { text: line.name, bold: true, color: nameColor },
      unitPriceCell(line, t, isOneTime),
      { text: String(line.qty), alignment: "right", color: line.included ? COLOR.body : COLOR.faint },
      { text: formatEur(line.amountCents), alignment: "right", bold: true, color: amountColor },
      {
        text: line.included ? t.included : t.notIncluded,
        alignment: "center",
        bold: true,
        color: statusColor,
        fontSize: 8.5,
      },
    ];
  });

  return [
    { text: heading, style: "sectionTitle", margin: [0, 18, 0, 6] },
    {
      table: {
        headerRows: 1,
        widths: ["*", 150, 32, 56, 48],
        body: [header, ...body],
      },
      layout: {
        // Dark underline beneath the header, hairline separators between rows.
        hLineWidth: (i: number) => (i === 0 ? 0 : i === 1 ? 1.2 : 0.5),
        hLineColor: (i: number) => (i === 1 ? COLOR.ink : COLOR.rule),
        vLineWidth: () => 0,
        paddingTop: (i: number) => (i === 0 ? 0 : 7),
        paddingBottom: (i: number) => (i === 0 ? 5 : 7),
        paddingLeft: () => 0,
        paddingRight: () => 8,
      },
    },
  ];
}

function metaTable(meta: QuoteDocumentMeta, t: DocTexts): Content {
  const rows: TableCell[][] = [];
  const push = (label: string, value: string | null) => {
    if (!value) return;
    rows.push([
      { text: label, color: COLOR.muted, alignment: "right", margin: [0, 0, 12, 0] },
      { text: value, bold: true, color: COLOR.ink, alignment: "right" },
    ]);
  };
  push(t.quoteNumberLabel, meta.quoteNumber);
  push(t.preparedLabel, meta.preparedAt);
  push(t.validUntilLabel, meta.validUntil);
  if (rows.length === 0) return { text: "" };
  return {
    table: { widths: ["auto", "auto"], body: rows },
    layout: "noBorders",
    fontSize: 9,
  };
}

function buildDocDefinition(opts: {
  state: CalculatorState;
  catalog: ProductCatalogItem[];
  language: ProposalLanguage;
  meta: QuoteDocumentMeta;
}): { def: TDocumentDefinitions; title: string } {
  const { state, catalog, language, meta } = opts;
  const t = DOC_TEXTS[language];
  const ct = getProposalTexts(language).calculator;

  const lines = buildQuoteDocumentLines(state, catalog, language);
  const recurring = lines.filter((l) => l.recurring);
  const oneTime = lines.filter((l) => !l.recurring);
  const totals = computeProposalTotals(state, catalog);
  const hasOneTime = oneTime.length > 0;

  const clientValue =
    meta.clientCompany && meta.clientPerson
      ? `${meta.clientCompany} — ${meta.clientPerson}`
      : meta.clientCompany ?? meta.clientPerson;
  const preparerValue = meta.preparerEmail
    ? `${meta.preparerName} — ${meta.preparerEmail}`
    : meta.preparerName;

  const generatedAt = new Date().toLocaleDateString(language === "lt" ? "lt-LT" : "en-GB");

  const title = meta.quoteNumber ? `${t.docTitle} ${meta.quoteNumber}` : t.docTitle;

  // Always break the monthly figure into fixed and variable rows so the PDF
  // clearly separates predictable charges from consumption-based ones.
  const monthlyRows: TableCell[][] = [
    [
      { text: t.totalMonthlyFixed, color: "#ffffff" },
      { text: formatEur(totals.monthlyFixed), color: "#ffffff", bold: true, alignment: "right" },
    ],
    [
      { text: t.totalMonthlyUsage, color: "#ffffff" },
      { text: formatEur(totals.monthlyUsage), color: "#ffffff", bold: true, alignment: "right" },
    ],
  ];
  const totalsRows: TableCell[][] = [
    ...monthlyRows,
    [
      { text: t.totalAnnual, color: "#ffffff" },
      { text: formatEur(totals.annual), color: "#ffffff", bold: true, alignment: "right" },
    ],
  ];
  if (hasOneTime) {
    totalsRows.push([
      { text: t.totalOneTime, color: "#ffffff" },
      { text: formatEur(totals.oneTime), color: "#ffffff", bold: true, alignment: "right" },
    ]);
  }

  const content: Content[] = [
    // Header — brand + document title on the left, dates on the right.
    {
      columns: [
        {
          width: "*",
          stack: [
            { text: "Noium", style: "brand" },
            { text: t.docTitle, style: "docTitle", margin: [0, 4, 0, 0] },
            { text: t.docSubtitle.toUpperCase(), style: "docSubtitle", margin: [0, 2, 0, 0] },
          ],
        },
        { width: "auto", stack: [metaTable(meta, t)] },
      ],
    },
    {
      canvas: [
        { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.4, lineColor: COLOR.ink },
      ],
      margin: [0, 14, 0, 0],
    },
    // Parties.
    {
      margin: [0, 16, 0, 0],
      columns: [
        clientValue
          ? {
              width: "*",
              stack: [
                { text: t.clientLabel.toUpperCase(), style: "partyLabel" },
                { text: clientValue, style: "partyValue" },
              ],
            }
          : { width: "*", text: "" },
        {
          width: "*",
          stack: [
            { text: t.preparedByLabel.toUpperCase(), style: "partyLabel" },
            { text: preparerValue, style: "partyValue" },
          ],
        },
      ],
    },
    ...serviceTable(t.recurringHeading, recurring, t, false),
    ...serviceTable(t.oneTimeHeading, oneTime, t, true),
    // Totals panel, right-aligned.
    {
      margin: [0, 22, 0, 0],
      columns: [
        { width: "*", text: "" },
        {
          width: 240,
          stack: [
            { text: t.totalsHeading.toUpperCase(), style: "totalsHeading", margin: [0, 0, 0, 5] },
            {
              table: { widths: ["*", "auto"], body: totalsRows },
              layout: {
                fillColor: () => COLOR.panel,
                hLineWidth: (i: number) => (i === 0 || i === totalsRows.length ? 0 : 0.5),
                hLineColor: () => COLOR.panelRule,
                vLineWidth: () => 0,
                paddingTop: () => 7,
                paddingBottom: () => 7,
                paddingLeft: () => 12,
                paddingRight: () => 12,
              },
            },
          ],
        },
      ],
    },
    // Notes.
    {
      margin: [0, 26, 0, 0],
      stack: [
        {
          canvas: [
            { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: COLOR.rule },
          ],
          margin: [0, 0, 0, 10],
        },
        { text: ct.legalNote, style: "note" },
        { text: ct.commitmentNote, style: "note", margin: [0, 4, 0, 0] },
        { text: t.generatedNote(generatedAt), style: "noteFaint", margin: [0, 10, 0, 0] },
      ],
    },
  ];

  const def: TDocumentDefinitions = {
    info: { title },
    pageSize: "A4",
    pageMargins: [40, 40, 40, 48],
    defaultStyle: { font: "Roboto", fontSize: 9, color: COLOR.body, lineHeight: 1.25 },
    styles: {
      brand: { fontSize: 20, bold: true, color: COLOR.ink },
      docTitle: { fontSize: 13, bold: true, color: COLOR.ink },
      docSubtitle: { fontSize: 8, bold: true, color: COLOR.muted, characterSpacing: 1 },
      partyLabel: { fontSize: 7.5, bold: true, color: COLOR.muted, characterSpacing: 0.8, margin: [0, 0, 0, 2] },
      partyValue: { fontSize: 10, bold: true, color: COLOR.ink },
      sectionTitle: { fontSize: 8, bold: true, color: COLOR.body, characterSpacing: 0.8 },
      colHead: { fontSize: 7.5, bold: true, color: COLOR.muted, characterSpacing: 0.6 },
      rowNote: { fontSize: 7.5, color: COLOR.muted, margin: [0, 2, 0, 0] },
      totalsHeading: { fontSize: 7.5, bold: true, color: COLOR.muted, characterSpacing: 0.8 },
      note: { fontSize: 8, color: COLOR.muted, lineHeight: 1.4 },
      noteFaint: { fontSize: 7.5, color: COLOR.faint },
    },
    footer: (currentPage: number, pageCount: number) => ({
      margin: [40, 12, 40, 0],
      columns: [
        { text: title, fontSize: 7.5, color: COLOR.faint },
        {
          text: t.pageLabel(currentPage, pageCount),
          fontSize: 7.5,
          color: COLOR.faint,
          alignment: "right",
        },
      ],
    }),
    content,
  };

  return { def, title };
}

function sanitizeFileName(name: string): string {
  return name
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Builds the download file name as `<company>_Noium_quote.pdf`, using the
// client company when available (e.g. "Acme Corp" -> "Acme_Corp_Noium_quote.pdf")
// and falling back to a plain "Noium_quote.pdf" when no company is set.
function buildDownloadFileName(clientCompany: string | null): string {
  const company = sanitizeFileName(clientCompany ?? "").replace(/\s+/g, "_");
  return company ? `${company}_Noium_quote.pdf` : "Noium_quote.pdf";
}

// Builds the financial summary PDF and triggers a file download. No popup, no
// print dialog — the browser saves the file directly. pdfmake is imported
// dynamically so the heavy library + fonts only load when a quote is actually
// downloaded, keeping it out of the main bundle.
export async function downloadQuotePdf(opts: {
  state: CalculatorState;
  catalog: ProductCatalogItem[];
  language: ProposalLanguage;
  meta: QuoteDocumentMeta;
}): Promise<void> {
  const [pdfMakeMod, vfsMod] = await Promise.all([
    import("pdfmake/build/pdfmake"),
    import("pdfmake/build/vfs_fonts"),
  ]);
  const pdfMake = (pdfMakeMod.default ?? pdfMakeMod) as unknown as PdfMake;
  const vfs = (vfsMod.default ?? vfsMod) as unknown as Record<string, string>;
  ensureFonts(pdfMake, vfs);
  const { def } = buildDocDefinition(opts);
  await pdfMake.createPdf(def).download(buildDownloadFileName(opts.meta.clientCompany));
}

export async function generateQuotePdfBase64(opts: {
  state: CalculatorState;
  catalog: ProductCatalogItem[];
  language: ProposalLanguage;
  meta: QuoteDocumentMeta;
}): Promise<{ base64: string; fileName: string }> {
  const [pdfMakeMod, vfsMod] = await Promise.all([
    import("pdfmake/build/pdfmake"),
    import("pdfmake/build/vfs_fonts"),
  ]);
  const pdfMake = (pdfMakeMod.default ?? pdfMakeMod) as unknown as PdfMake;
  const vfs = (vfsMod.default ?? vfsMod) as unknown as Record<string, string>;
  ensureFonts(pdfMake, vfs);
  const { def } = buildDocDefinition(opts);
  const fileName = buildDownloadFileName(opts.meta.clientCompany);
  const base64 = await pdfMake.createPdf(def).getBase64();
  return { base64, fileName };
}
